
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useCrossDisciplinaryEdges } from '@/hooks/useCrossDisciplinaryEdges';
import { useNodes } from '@/hooks/useNodes';
import { Network } from 'lucide-react';
import EdgeCreationForm from './cross-disciplinary/EdgeCreationForm';
import PendingApprovalList from './cross-disciplinary/PendingApprovalList';
import ApprovedEdgesList from './cross-disciplinary/ApprovedEdgesList';

const CrossDisciplinaryManager: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterRelationship, setFilterRelationship] = useState<string>('all');

  const { data: allNodes } = useNodes();
  const { data: allEdges, refetch: refetchEdges } = useCrossDisciplinaryEdges();

  const publishedNodes = allNodes?.filter(node => node.status === 'published') || [];

  const getEdgeStatus = (metadata: any): string => {
    if (!metadata) return "approved";
    if (typeof metadata === "object" && "status" in metadata) {
      return (metadata.status ?? "approved") as string;
    }
    if (typeof metadata === "string") {
      try {
        const obj = JSON.parse(metadata);
        if (obj && typeof obj === "object" && "status" in obj) return obj.status;
      } catch {
        return "approved";
      }
    }
    return "approved";
  };

  const getNodeById = (nodeId?: string | null) => {
    if (!nodeId) return null;
    return publishedNodes.find(node => node.id === nodeId);
  };

  // Filter edges based on current filters
  const filteredEdges = allEdges?.filter(edge => {
    const matchesRelationship = filterRelationship === 'all' || edge.relationship_type === filterRelationship;
    const matchesSearch = !searchTerm || 
      (getNodeById(edge.source_node_id)?.title?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (getNodeById(edge.target_node_id)?.title?.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesRelationship && matchesSearch;
  }) || [];

  const pendingEdges = filteredEdges.filter(edge => getEdgeStatus(edge.metadata) === 'pending');
  const approvedEdges = filteredEdges.filter(edge => 
    getEdgeStatus(edge.metadata) === 'approved' || 
    getEdgeStatus(edge.metadata) === undefined || 
    getEdgeStatus(edge.metadata) === null
  );

  const handleRefreshEdges = () => {
    refetchEdges();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Network className="h-6 w-6" />
        <h2 className="text-2xl font-bold">Cross-Disciplinary Mapping</h2>
      </div>

      <Tabs defaultValue="create" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="create">Create Connections</TabsTrigger>
          <TabsTrigger value="pending" className="relative">
            Pending Approval
            {pendingEdges.length > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 text-xs">
                {pendingEdges.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="manage">Manage Connections</TabsTrigger>
        </TabsList>

        <TabsContent value="create">
          <EdgeCreationForm onEdgeCreated={handleRefreshEdges} />
        </TabsContent>

        <TabsContent value="pending">
          <PendingApprovalList
            edges={pendingEdges}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filterRelationship={filterRelationship}
            onRelationshipFilterChange={setFilterRelationship}
            onEdgeUpdated={handleRefreshEdges}
          />
        </TabsContent>

        <TabsContent value="manage">
          <ApprovedEdgesList
            edges={approvedEdges}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filterRelationship={filterRelationship}
            onRelationshipFilterChange={setFilterRelationship}
            onEdgeDeleted={handleRefreshEdges}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CrossDisciplinaryManager;
